import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
    Button, Alert, CircularProgress
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import { ReservationService } from '../api/services/reservationService';

export default function NoShowReservationModal({ open, onClose, reservation, tenantId, onSuccess, setNotification }) {
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            setError(null);
        }
    }, [open]);

    const handleConfirm = async () => {
        setIsProcessing(true);
        setError(null);
        try {
            await ReservationService.markNoShow(reservation.id, tenantId);
            if (setNotification) {
                setNotification({ open: true, message: 'Reservation successfully flagged as No-Show. Penalties processed and inventory released.', severity: 'success' });
            }
            if (onSuccess) onSuccess();
            onClose();
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'Failed to mark reservation as No-Show';
            if (setNotification) {
                setNotification({ open: true, message: msg, severity: 'error' });
            } else {
                setError(msg);
            }
        } finally {
            setIsProcessing(false);
        }
    };

    if (!reservation) return null;

    return (
        <Dialog 
            open={open} 
            onClose={isProcessing ? undefined : onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, padding: 1 } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold', color: 'warning.main' }}>
                <BlockIcon /> Mark No-Show
            </DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
                <DialogContentText sx={{ color: 'text.primary' }}>
                    Are you sure you want to mark this reservation as a No-show? This action will permanently modify the booking and all related room allocations to No-show status. The system will automatically resolve the active cancellation policy, calculate penalty fees using your configured No-Show rules, apply them to the billing ledger, and release the physical rooms back to Available status instantly.
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ padding: '16px 24px' }}>
                <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }} disabled={isProcessing}>
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color="warning"
                    sx={{ borderRadius: 2 }}
                    disabled={isProcessing}
                >
                    {isProcessing ? <CircularProgress size={20} color="inherit" /> : 'Confirm No-Show'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
